import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { FileText, PlayCircle, Share2, BarChart3, RefreshCw, Wand2, CheckCircle2 } from 'lucide-react';
interface SlideWhatSnakkerDoesProps {
  onContinue: () => void;
}
export function SlideWhatSnakkerDoes({
  onContinue
}: SlideWhatSnakkerDoesProps) {
  const [stage, setStage] = useState(0);
  const steps = [{
    id: 'ingest',
    icon: <FileText className="w-6 h-6 text-teal-400" data-id="element-1471" data-mp="2w00hx2cdctn6" />,
    title: 'Ingest',
    desc: 'Takes existing approved content'
  }, {
    id: 'transform',
    icon: <Wand2 className="w-6 h-6 text-purple-400" data-id="element-1472" data-mp="35x5617pryz6m" />,
    title: 'Transform',
    desc: 'Turns them into short interactive Snaks'
  }, {
    id: 'publish',
    icon: <Share2 className="w-6 h-6 text-sky-400" data-id="element-1473" data-mp="1bikq18cefrtk" />,
    title: 'Publish',
    desc: 'Distributes across all channels'
  }, {
    id: 'learn',
    icon: <BarChart3 className="w-6 h-6 text-rose-400" data-id="element-1474" data-mp="1giqn541fu6j1" />,
    title: 'Learn',
    desc: 'Captures behavioral data'
  }, {
    id: 'improve',
    icon: <RefreshCw className="w-6 h-6 text-amber-400" data-id="element-1475" data-mp="33hl48uj9xrma" />,
    title: 'Improve',
    desc: 'Optimizes the next campaign'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 400);
    const t2 = setTimeout(() => setStage(2), 1000);
    const timers = steps.map((_, idx) => setTimeout(() => setStage(3 + idx), 1500 + idx * 350));
    const finalTimer = setTimeout(() => setStage(3 + steps.length), 1500 + steps.length * 350 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
      clearTimeout(finalTimer);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-6xl mx-auto" data-id="element-1476" data-mp="3daihal5hyodi">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-3 shrink-0" data-id="element-1477" data-mp="21taeu5eevibz">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1478" data-mp="28a2h8mnpkbh6">
          Turn-key
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1479" data-mp="1tb8eysrs9c5f">
          Delivered ready to go live — not a DIY project
        </h2>
        <p className="text-lg text-white/60" data-id="element-1480" data-mp="1ydfx6037hog7">
          Snakker runs every step. Affiliates and partners receive a finished
          campaign.
        </p>
      </motion.div>

      {/* Flow diagram */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 20
    }} transition={{
      duration: 0.6
    }} className="flex-1 min-h-0 flex items-center justify-center mt-6" data-id="element-1481" data-mp="1pcrh6zu95e0o">
        <div className="w-full h-full rounded-2xl bg-white border border-white/10 shadow-2xl overflow-hidden flex items-center justify-center" data-id="element-1482" data-mp="15mfpnf5o22js">
          <img src="https://cdn.magicpatterns.com/uploads/aDhBbAFowWKRHKXg1acHd3/Screenshot_2026-05-28_at_11.46.20.png" alt="Flow Example Campaign — Hook to Snak to Insights" className="w-full h-full object-contain block" data-id="element-1483" data-mp="myx5qtx6ksp9" />
        </div>
      </motion.div>

      {/* Turn-key split */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: stage >= 3 ? 1 : 0,
      y: stage >= 3 ? 0 : 12
    }} className="shrink-0 grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-4 mt-5" data-id="element-1484" data-mp="r2bnbp0rph4t">
        <div className="bg-teal-400/[0.07] border border-teal-400/40 rounded-2xl p-5" data-id="element-1485" data-mp="1eort4l1ha8ba">
          <p className="text-teal-400 text-[11px] font-semibold uppercase tracking-widest mb-3" data-id="element-1486" data-mp="1aohr1jgt421p">
            Snakker delivers, end to end
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-1487" data-mp="1jowdwn9f5p7b">
            {['Snakkerfy the approved content', 'Localize per market', 'Build the campaign logic', 'Publish and distribute', 'Track engagement', 'Report and optimize'].map(item => <div key={item} className="flex items-start gap-2 text-white/85 text-xs bg-navy-900/50 border border-navy-700 rounded-lg px-2.5 py-2 leading-snug" data-id="element-1488" data-mp="2cjm1f61gfqa7">
                <CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-px" data-id="element-1489" data-mp="1ktzgecvnlxl4" />
                {item}
              </div>)}
          </div>
        </div>

        <div className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5 flex flex-col" data-id="element-1490" data-mp="1trtadh5y4f54">
          <p className="text-white/40 text-[11px] font-semibold uppercase tracking-widest mb-3" data-id="element-1491" data-mp="1ysparkr39j5s">
            The affiliate only
          </p>
          <ul className="space-y-2 flex-1" data-id="element-1492" data-mp="35a44w863bto5">
            {['Reviews and signs off', 'Says go'].map(item => <li key={item} className="flex items-start gap-2.5 text-white/85 text-sm" data-id="element-1493" data-mp="yjhkijejktn5">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0 mt-1.5" data-id="element-1494" data-mp="tlfqj3fkv9ww" />
                {item}
              </li>)}
          </ul>
          <p className="text-white/50 text-xs mt-3 pt-3 border-t border-navy-700 leading-relaxed" data-id="element-1495" data-mp="jul49z2c0abg">
            No brief to write, no local project, no vendor to onboard, no hours
            to find.
          </p>
        </div>
      </motion.div>
    </div>;
}